import { useEffect, useRef, useState } from "react";
import { caseFileProxyBlob, standingOrderAttachmentBlob } from "@/lib/api";
import { classifyBlob, openBlobInNewTab } from "@/lib/safeBlob";

// Files behind the API cannot be reached by a plain link or an iframe: a
// navigation sends no Authorization header, so under a Clerk session the
// default-deny router answers 401. They are fetched through the API client
// (bearer token, cookie, CSRF) and shown from an object URL instead.

/**
 * Fetches a standing-order email attachment through the API client and opens it in a new tab, or
 * downloads it when its type is not one the browser may render (see `safeBlob`).
 * Rejects when the request fails, so the caller can say so.
 *
 * @param {string} appId application id or key.
 * @param {string} attId attachment id.
 * @param {string} [filename] download name if the pop-up is blocked.
 */
export const openStandingOrderAttachment = async (appId, attId, filename) =>
  openBlobInNewTab(await standingOrderAttachmentBlob(appId, attId), filename);

/** What `useCaseFileBlobUrl` reports before anything has been asked for. */
const IDLE = { status: "idle", url: null, kind: null, error: null };
/** What it reports from the moment a new file is requested until it answers. */
const LOADING = { status: "loading", url: null, kind: null, error: null };

/** A short message for a failed file request, naming the HTTP status when there is one. */
const describeFailure = (err) => {
  const status = err?.response?.status;
  return status ? `Couldn't load the file (HTTP ${status}).` : "Couldn't load the file.";
};

/**
 * Loads a file through `load` and exposes it as an object URL.
 *
 * `status` is `idle` (no `key`), `loading`, `ready` (`url` and `kind` set) or `error` (`error`
 * set), so a caller never renders a frame with nothing behind it. `kind` comes from `classifyBlob`
 * and is decided by the blob's own type: `pdf`, `image`, `text`, or `download` for anything the
 * browser must not render. The object URL is of the re-wrapped blob, never of the served type.
 * It is revoked when the key changes and on unmount, the request is cancelled then too, and a
 * response that arrives after either is discarded. The result is keyed, so a changed file reads
 * `loading` immediately rather than for one render showing the previous file's revoked URL.
 *
 * Every preview of a file the API serves should go through this and `components/BlobPreview.jsx`:
 * the object URL is same-origin with the portal, so what may be framed is decided here.
 *
 * @param {string|null|undefined} key identifies the file; a new key loads a new file, none loads nothing.
 * @param {(signal: AbortSignal) => Promise<Blob>} load fetches the file through the API client. Only
 *   the latest function is called, and only when the key changes. Pass `signal` on to the request:
 *   it is aborted when the key changes or the component unmounts, which is what cancels the download.
 * @returns {{status: "idle"|"loading"|"ready"|"error", url: string|null, kind: "pdf"|"image"|"text"|"download"|null, error: string|null}}
 */
export const useBlobUrl = (key, load) => {
  const [settled, setSettled] = useState({ forKey: null, view: IDLE });
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    let objectUrl = null;
    const controller = new AbortController();
    (async () => loadRef.current(controller.signal))()
      .then((blob) => {
        if (!alive) return;
        const safe = classifyBlob(blob);
        objectUrl = URL.createObjectURL(safe.blob);
        setSettled({ forKey: key, view: { status: "ready", url: objectUrl, kind: safe.kind, error: null } });
      })
      .catch((err) => {
        if (alive) {
          setSettled({ forKey: key, view: { status: "error", url: null, kind: null, error: describeFailure(err) } });
        }
      });
    return () => {
      alive = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key]);

  if (!key) return IDLE;
  return settled.forKey === key ? settled.view : LOADING;
};

/**
 * Loads a collections case file through the API client; see `useBlobUrl`.
 *
 * @param {string|null|undefined} proxyUrl the case file's server-supplied `proxy_url`, or none.
 */
export const useCaseFileBlobUrl = (proxyUrl) =>
  useBlobUrl(proxyUrl || null, (signal) => caseFileProxyBlob(proxyUrl, signal));
